import { useState, useRef, ChangeEvent, KeyboardEvent, useEffect } from "react";
import { Send, Search, UploadIcon, Info, Trash2 } from "lucide-react";
import axios from "axios";
// import Loader from "~/components/ui/loaders/genericLoader";
import { AutoExpandingTextarea } from "../../components/UI/AutoExpandTextArea";
import { cn } from "../../helpers/shared";
// import { useGetCrewAiAgents } from "../../hooks/connectors/useAgentSDKApi";
import {
  AGENT_ACTIONS_BASE_URL,
  INTEGRATION_RESOURCES,
} from "../../constants/apiUrl";

const ChatInput = ({
  onSubmit,
  setSelectedAgent,
  selectedAgent,
  placeholder = "Ask an agent to create something... Type @ to mention an agent",
  placement = "bottom",
  setSelectedFile,
  selectedFile,
  agentName,
  agentId,
  agentDetails,
  agentLists,
  agentsLoading
}) => {
  const uploadResources = "";
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const agentFiltersState = "";
  const [agentsData, setAgentsData] = useState([]);
  const agents = agentLists || [];
  const llmAgents =
    agents
      ?.filter((item) => item?.agent_config?.llm !== null)
      ?.sort((a, b) =>
        a?.agent_config?.name.localeCompare(b?.agent_config?.name)
      ) || [];
  useEffect(() => {
    setAgentsData(llmAgents);
    setFilteredAgents(llmAgents);
  }, [agents]);

  const fileInputRef = useRef(null);
  //const [selectedFile, setSelectedFile] = useState<File | null>(null);
  useEffect(() => {
    if (agentDetails) {
      const textWithoutAgent = inputValue
        .replace(`@${agentName}`, "")
        .trim()?.length;

      if (textWithoutAgent > 0) {
        setSubmitDisabled(false);
      } else {
        setSubmitDisabled(true);
      }
    } else {
      setSubmitDisabled(true);
    }
  }, [agentDetails, inputValue]);

  useEffect(() => {
    if (agentDetails) {
      const selectedTools = [];
      agentDetails?.agent_config?.tasks?.forEach((item) => {
        if (item.tools) {
          const toolsName = item?.tools?.tool_definitions?.map(
            (ele) => ele.integration_name
          );
          selectedTools?.push(...toolsName);
        }
      });
      if (selectedTools?.length) {
        getResourcesForIntegration(selectedTools);
      }
    } else {
      setSubmitDisabled(true);
    }
  }, [agentDetails]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart;

    if (!newValue) {
      //setSelectedAgent(null);
      setShowDropdown(false);
      setInputValue(newValue);
      return;
    }

    setInputValue(newValue);

    // Check if cursor is positioned right after '@'
    // Look backwards from cursor to find '@' and check if there's a space or newline before it
    let shouldShowDropdown = false;
    let searchStartIndex = cursorPosition;

    // Find the '@' symbol before cursor position
    for (let i = cursorPosition - 1; i >= 0; i--) {
      if (newValue[i] === "@") {
        // Check if '@' is at start or preceded by whitespace/newline
        if (i === 0 || /\s/.test(newValue[i - 1])) {
          shouldShowDropdown = false;
          searchStartIndex = i + 1;
          break;
        }
      } else if (/\s/.test(newValue[i])) {
        // Stop if we hit whitespace before finding '@'
        break;
      }
    }

    if (shouldShowDropdown) {
      setShowDropdown(false);
      // Filter agents based on text between '@' and cursor
      const searchTerm = newValue
        .substring(searchStartIndex, cursorPosition)
        .trim()
        .toLowerCase();
      const filtered = agentsData.filter((agent) =>
        agent?.agent_config?.name.toLowerCase().includes(searchTerm)
      );
      setFilteredAgents(filtered);
    } else {
      setShowDropdown(false);
    }
  };

  const handleSubmit = () => {
    // if (inputValue.trim() && !disabled) {
    onSubmit?.(inputValue.trim());
    setInputValue("");
    //onChange?.('');
    //}
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    // if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
    //   e.preventDefault();
    //   handleSubmit();
    // }
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (!submitDisabled) {
        handleSubmit();
      }
    }
    // Close dropdown on Escape
    if (e.key === "Escape") {
      setShowDropdown(false);
    }

    // Check cursor position on arrow keys or other navigation keys
    if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
      // Use setTimeout to check cursor position after the key event has processed
      setTimeout(() => {
        const textarea = textareaRef.current;
        if (textarea) {
          const cursorPosition = textarea.selectionStart;
          const newValue = textarea.value;

          // Check if cursor is positioned right after '@'
          let shouldShowDropdown = false;
          let searchStartIndex = cursorPosition;

          for (let i = cursorPosition - 1; i >= 0; i--) {
            if (newValue[i] === "@") {
              if (i === 0 || /\s/.test(newValue[i - 1])) {
                shouldShowDropdown = false;
                searchStartIndex = i + 1;
                break;
              }
            } else if (/\s/.test(newValue[i])) {
              break;
            }
          }

          if (shouldShowDropdown) {
            setShowDropdown(false);
            const searchTerm = newValue
              .substring(searchStartIndex, cursorPosition)
              .trim()
              .toLowerCase();
            const filtered = agentsData.filter((agent) =>
              agent?.agent_config?.name.toLowerCase().includes(searchTerm)
            );
            setFilteredAgents(filtered);
          } else {
            setShowDropdown(false);
          }
        }
      }, 0);
    }
  };

  const handleMentionClick = () => {
    const newValue = inputValue + "@";
    setInputValue(newValue);
    //onChange?.(newValue);
    setTimeout(() => {
      textareaRef.current?.focus();
      // Move cursor to end
      const length = textareaRef.current?.value.length || 0;
      textareaRef.current?.setSelectionRange(length, length);
    }, 0);
  };
  const getResourcesForIntegration = async (selectedTools) => {
    try {
      const payload = {
        integrations: selectedTools,
      };

      const response = await axios.post(
        `${AGENT_ACTIONS_BASE_URL}/${INTEGRATION_RESOURCES}`,
        payload
      );

      const data = response?.data;

      if (data) {
        const requiredTypeData = data.filter((item) => item?.resource_required);

        return requiredTypeData; // optional: return it if needed
      }

      return [];
    } catch (error) {
      console.error("Failed to get integration resources:", error);
      throw error; // optional
    }
  };
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (file) {
      setSelectedFile?.(file);
    }
  };
  // Handle agent selection
  const handleAgentSelect = (agent) => {
    // const selectedTools: string[] = [];

    // agent?.agent_config?.tasks?.forEach((item) => {
    //     if (item.tools) {
    //         const toolsName = item?.tools?.tool_definitions?.map((ele) => ele.integration_name);
    //         selectedTools?.push(...toolsName);
    //     }
    // });
    // if (selectedTools?.length) {
    //     getResourcesForIntegration(selectedTools);
    // }

    setSelectedAgent?.(agent);
    setInputValue(`@${agent?.agent_config?.name} `);
    setShowDropdown(false);
    // Focus back to input for description
    setTimeout(() => textareaRef.current?.focus(), 100);
  };

  const renderAgentsList = () => {
    if (agentsLoading) {
      return (
        <div className="flex h-full w-full items-center justify-center">
          <p>Loading...</p>
        </div>
      );
    } else if (filteredAgents?.length > 0) {
      return (
        <>
          {filteredAgents?.map((agent) => (
            <div
              key={agent.id}
              onClick={() => handleAgentSelect(agent)}
              className="cursor-pointer border-b border-gray-100 px-4 py-3 last:border-b-0 hover:bg-blue-50"
            >
              <div className="font-medium text-gray-800">
                {agent?.agent_config?.name}
              </div>
            </div>
          ))}
        </>
      );
    } else {
      return (
        <div className="px-4 py-3 text-center text-gray-500">
          <Search size={16} className="mx-auto mb-2" />
          No agents found
        </div>
      );
    }
  };

  return (
    <div
      className="relative w-full rounded-lg border border-[#15808C]/40 bg-white p-5 shadow-sm"
      style={{
        animation: "borderGlow 2s ease-in-out infinite",
      }}
    >
      <div className="relative flex items-end gap-3">
        <div className="flex-1">
          <AutoExpandingTextarea
            ref={textareaRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onSelect={(e) => {
              // Check cursor position when selection changes (e.g., clicking)
              const target = e.target;
              const cursorPosition = target.selectionStart;
              const newValue = target.value;

              let shouldShowDropdown = false;
              let searchStartIndex = cursorPosition;

              for (let i = cursorPosition - 1; i >= 0; i--) {
                if (newValue[i] === "@") {
                  if (i === 0 || /\s/.test(newValue[i - 1])) {
                    shouldShowDropdown = false;
                    searchStartIndex = i + 1;
                    break;
                  }
                } else if (/\s/.test(newValue[i])) {
                  break;
                }
              }

              if (shouldShowDropdown) {
                setShowDropdown(false);
                const searchTerm = newValue
                  .substring(searchStartIndex, cursorPosition)
                  .trim()
                  .toLowerCase();
                const filtered = agentsData.filter((agent) =>
                  agent?.agent_config?.name.toLowerCase().includes(searchTerm)
                );
                setFilteredAgents(filtered);
              } else {
                setShowDropdown(false);
              }
            }}
            placeholder={placeholder}
            //disabled={disabled}
            minRows={2}
            maxRows={8}
            className="w-full resize-none border-none"
          />
        </div>
        {showDropdown && (
          <div
            ref={dropdownRef}
            className={`absolute ${
              placement === "top" ? "bottom-15" : "top-10"
            } z-10 max-h-60 w-[93%] overflow-y-auto rounded-lg rounded-t-[0] border border-gray-300 bg-white shadow-lg`}
          >
            <div className="py-2">
              <div className="border-b px-3 py-2 text-xs font-medium tracking-wide text-gray-500 uppercase">
                Available Agents
              </div>
              {renderAgentsList()}
            </div>
          </div>
        )}
        {/* <div className="relative flex items-center gap-1 group">
          <UploadIcon
            color={uploadResources?.enabled ? "black" : "gray"}
            cursor={uploadResources?.enabled ? "pointer" : "not-allowed"}
            onClick={() => {
              if (!uploadResources?.enabled) return;
              fileInputRef.current && (fileInputRef.current.value = "");
              fileInputRef.current?.click();
            }}
          />
          {uploadResources?.enabled && (
            <>
              <Info className="h-4 w-4 text-gray-400 cursor-help" />
              <div className="absolute right-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                <div className="font-semibold mb-1">
                  📎 File Upload Available
                </div>
                <div className="text-gray-300">
                  This agent supports presentation templates. Upload files (
                  {uploadResources?.resource_type?.join(", ") ||
                    "supported formats"}
                  ) to enhance your request.
                </div>
              </div>
            </>
          )}
        </div> */}
        <input
          ref={fileInputRef}
          style={{ display: "none" }}
          type="file"
          accept={`${uploadResources?.resource_type?.join(",")}`}
          onChange={handleFileChange}
        />
        {/* Send Button */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitDisabled}
          size="icon"
          className={cn(
            "h-10 w-10 shrink-0 rounded-lg transition-colors flex justify-center items-center",
            !submitDisabled
              ? "bg-[#15808C] text-white hover:bg-[#15808C]/90"
              : "cursor-not-allowed bg-gray-200 text-gray-400"
          )}
        >
          <Send
            className={cn(
              "h-5 w-5",
              !submitDisabled ? "text-white" : "text-gray-400"
            )}
          />
        </button>
      </div>
      {/* {!inputValue && ( */}
      {/* <div
        onClick={handleMentionClick}
        className="mb-3 mt-2 inline-flex cursor-pointer items-center gap-1.5 text-xs text-gray-600 transition-colors hover:bg-gray-200"
      >
        <span className="rounded-md bg-gray-100 px-3 py-1.5 font-medium">
          @
        </span>
        <span>Mention an agent</span>
      </div> */}
      <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
        <span>💡 Tip: Press Ctrl+Enter (Cmd+Enter on Mac) to submit</span>
        <div className="flex items-center gap-1">
          {selectedFile?.name && (
            <span>File selected: {selectedFile?.name}</span>
          )}
          {selectedFile?.name && (
            <button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedFile(null);
              }}
              className="h-4 text-red-500 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      {/* )} */}
    </div>
  );
};

export default ChatInput;
